// Defect Detection — canvas-first workspace that embeds in the Inspect tab.
// Store with autosave toast, editor/confirm modals, deleted-defects bin,
// map/table layout toggle.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  apiCreateDefect, apiCreatePhotoDefect, apiDeleteDefect, apiPurgeDefect, apiResetDefect, apiRestoreDefect,
  apiUpdateDefect, apiUploadPhotoSurface,
  type ApiDefect, type ApiPhoto,
} from "../data/api";
import type { OrgUser } from "../data/model";
import { shapeCentroid, translateShape, type ActiveOrtho } from "./geo";
import { IcCamera, IcCheck, IcChev, IcEye, IcEyeOff, IcList, IcPlus, IcTrash, IcUndo, IcWarnTri, IcX } from "./icons";
import { DefectList } from "./list";
import { CAT_ORDER, catOf, catLabel, DefectForm, SEV_ORDER, SEVERITY, setDefectUsers, sevMeta, userName, type CatFilterValue, type DefectDraft, type SevFilterValue } from "./meta";
import { Model3DView } from "./model3d";
import { SourceGallery } from "./sources";
import { DefectDetail, DefectTable, WorkspaceModeToggle } from "./table";
import type { Defect, DefectShape, DeletedDefect, FocusState, MarkTool, PhotoSurface, SourceImage } from "./types";
import { IcImage, IcCube, IcLayers } from "./icons";
import { Legend, ToolPalette, Viewer, ViewSwitcher, type ViewDef } from "./viewer";

// Ad hoc photo surfaces live in the workspace's view state as "photo:<id>";
// a defect's `view` field holds the bare surface id (absent = orthomosaic).
const PHOTO_PREFIX = "photo:";
// The empty photos surface (no photo selected yet) — reachable so the first
// ad hoc photo can be uploaded even when the inspection has none.
const PHOTOS_EMPTY = "photos";
const asPhotoView = (id: string) => PHOTO_PREFIX + id;
const photoIdOf = (view: string) => (view.startsWith(PHOTO_PREFIX) ? view.slice(PHOTO_PREFIX.length) : null);

// The defect meta helpers resolve author/editor ids against the live org
// member list; an unknown id renders "Unknown" (e.g. a removed member).


// ── API wiring ────────────────────────────────────────────────────────
export interface WsOrtho extends ActiveOrtho { view: string; label: string }

export function apiDefectToLocal(d: ApiDefect): Defect {
  return {
    id: d.id, orthoViewId: d.orthoViewId, view: d.view, ref: d.ref, title: d.title, category: d.category,
    severity: d.severity, confidence: d.confidence, x: d.x, y: d.y, shape: d.shape,
    desc: d.desc, action: d.action, source: d.source, modified: d.modified,
    addedBy: d.addedBy, editedBy: d.editedBy, orig: d.orig as Defect["orig"],
    sourceImages: d.sourceImages,
  };
}
export const apiDefectToDeleted = (d: ApiDefect): DeletedDefect =>
  ({ ...apiDefectToLocal(d), deletedAt: d.deletedAt ?? Date.now(), deletedBy: d.deletedBy });

export interface WorkspaceData {
  orgId: string;
  inspectionId: string;
  orthos: WsOrtho[];
  modelUrl: string | null;
  initialDefects: Defect[];
  initialDeleted: DeletedDefect[];
  photos: ApiPhoto[];
  photoSurfaces: PhotoSurface[];
  currentUserId: string;
}

const BLANK_API: ApiDefect = {
  id: "", orthoViewId: "", ref: 0, title: "New defect", desc: "", action: "", category: "other",
  severity: "moderate", confidence: null, x: 0, y: 0, source: "user", modified: false, sourceImages: [],
};

// ── Store ─────────────────────────────────────────────────────────────
type SaveStatus = "idle" | "saving" | "saved";

function useDefectStore(data: WorkspaceData) {
  const { orgId, inspectionId } = data;
  const [defects, setDefects] = useState<Defect[]>(data.initialDefects);
  const [view, setView] = useState<string>(data.orthos[0]?.id ?? "model");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tool, setTool] = useState<MarkTool>("select");
  const [movingId, setMovingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterSev, setFilterSev] = useState<SevFilterValue>("all");
  const [filterCat, setFilterCat] = useState<CatFilterValue>("all");
  const [focus, setFocus] = useState<FocusState>({ id: null, seq: 0 });
  const nextRef = useRef(100);
  const [deleted, setDeleted] = useState<DeletedDefect[]>(data.initialDeleted);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const saveTimer = useRef<{ a?: ReturnType<typeof setTimeout>; b?: ReturnType<typeof setTimeout> }>({});
  // Real autosave: every mutation applies optimistically and the toast
  // tracks the API round trip. Failures log and clear the toast (the
  // optimistic state stays; a reload shows server truth).
  const track = (p: Promise<unknown>) => {
    setSaveStatus("saving");
    clearTimeout(saveTimer.current.a);
    clearTimeout(saveTimer.current.b);
    p.then(() => {
      setSaveStatus("saved");
      saveTimer.current.b = setTimeout(() => setSaveStatus("idle"), 1900);
    }).catch((e) => {
      console.error("defect save failed:", e);
      setSaveStatus("idle");
    });
  };
  useEffect(() => () => { clearTimeout(saveTimer.current.a); clearTimeout(saveTimer.current.b); }, []);
  const defectsRef = useRef<Defect[]>(defects);
  defectsRef.current = defects;

  const select = (id: string | null) => { setSelectedId(id); setMovingId(null); };
  // Selecting a defect from the list (or remediation "locate") jumps to the
  // surface it lives on — an ortho or an ad hoc photo — then flies to it.
  const focusDefect = (id: string) => {
    const d = defectsRef.current.find((x) => x.id === id);
    if (d) {
      const target = d.view ? asPhotoView(d.view) : (d.orthoViewId && data.orthos.some((o) => o.id === d.orthoViewId) ? d.orthoViewId : view);
      if (target !== view && target !== "model") setView(target);
    }
    setSelectedId(id);
    setMovingId(null);
    setFocus((f) => ({ id, seq: f.seq + 1 }));
  };
  const orthoIdForView = () => (data.orthos.some((o) => o.id === view) ? view : data.orthos[0]?.id);
  const nextRefNum = () => Math.max(0, ...defects.map((d) => d.ref)) + 1;
  // Create a defect on the active surface (ortho or ad hoc photo). Photo-tagged
  // defects go through the no-georeference endpoint; their source is the photo.
  const addDefect = async (body: { x: number; y: number; shape?: DefectShape }): Promise<Defect | null> => {
    const pid = photoIdOf(view);
    const payload = { title: "New defect", category: "other", severity: "moderate", desc: "", ...body };
    if (pid) {
      const p = apiCreatePhotoDefect(orgId, inspectionId, pid, payload);
      track(p);
      const nd = apiDefectToLocal((await p.catch(() => null))?.defect ?? { ...BLANK_API, orthoViewId: undefined, view: pid, ref: nextRefNum(), id: "u-" + nextRef.current++, ...body });
      setDefects((prev) => [...prev, nd]);
      setTool("select");
      setSelectedId(nd.id);
      return nd;
    }
    const orthoId = orthoIdForView();
    if (!orthoId) return null;
    const p = apiCreateDefect(orgId, inspectionId, orthoId, payload);
    track(p);
    const nd = apiDefectToLocal((await p.catch(() => null))?.defect ?? { ...BLANK_API, orthoViewId: orthoId, ref: nextRefNum(), id: "u-" + nextRef.current++, ...body });
    setDefects((prev) => [...prev, nd]);
    setTool("select");
    setSelectedId(nd.id);
    return nd;
  };
  const addAt = (x: number, y: number) => addDefect({ x, y });
  // A drawn marking (box or outline). Anchor x/y = centroid so the popover,
  // fly-to, and move interactions work identically to point defects.
  const addShape = (shape: DefectShape) => { const [cx, cy] = shapeCentroid(shape); return addDefect({ x: cx, y: cy, shape }); };
  // Replace an outlined defect's shape with a freshly drawn one (Redraw).
  const updateShape = (id: string, shape: DefectShape) => {
    const [cx, cy] = shapeCentroid(shape);
    track(apiUpdateDefect(orgId, inspectionId, id, { x: cx, y: cy, shape }));
    setDefects((p) => p.map((d) => {
      if (d.id !== id) return d;
      const merged: Defect = { ...d, shape, x: cx, y: cy };
      if (d.source === "ai") { merged.modified = true; merged.editedBy = data.currentUserId; merged.seeded = false; }
      return merged;
    }));
    setTool("select");
    setSelectedId(id);
  };
  // Live drag updates the position but does NOT flag a save — the autosave
  // toast only fires once the drag is committed (commitMove, on dragend).
  const moveDefect = (id: string, x: number, y: number) => setDefects((p) => p.map((d) => {
    if (d.id !== id) return d;
    const modified = d.source === "ai" && d.orig ? Math.abs(x - d.orig.x) > 0.4 || Math.abs(y - d.orig.y) > 0.4 || d.modified : d.modified;
    const out: Defect = { ...d, x, y, modified };
    if (d.shape) out.shape = translateShape(d.shape, x - d.x, y - d.y); // shape moves with its anchor
    if (d.source === "ai" && modified) { out.editedBy = data.currentUserId; out.seeded = false; }
    return out;
  }));
  const commitMove = () => {
    const id = movingId ?? selectedId;
    const d = id ? defectsRef.current.find((v) => v.id === id) : undefined;
    if (d) track(apiUpdateDefect(orgId, inspectionId, d.id, { x: d.x, y: d.y, shape: d.shape ?? null }));
  };
  const updateDefect = (id: string, patch: Partial<Defect>) => {
    const cur = defectsRef.current.find((d) => d.id === id);
    if (cur) {
      const m = { ...cur, ...patch };
      track(apiUpdateDefect(orgId, inspectionId, id, { title: m.title, category: m.category ?? "other", severity: m.severity, desc: m.desc, x: m.x, y: m.y, shape: m.shape ?? null }));
    }
    setDefects((p) => p.map((d) => {
      if (d.id !== id) return d;
      const merged: Defect = { ...d, ...patch };
      if (d.source === "ai" && d.orig) {
        merged.modified = merged.title !== d.orig.title || (merged.category || "other") !== (d.orig.category || "other") || merged.severity !== d.orig.severity || merged.desc !== d.orig.desc || Math.abs(merged.x - d.orig.x) > 0.4 || Math.abs(merged.y - d.orig.y) > 0.4;
        if (merged.modified) { merged.editedBy = data.currentUserId; merged.seeded = false; }
      }
      return merged;
    }));
  };
  const resetDefect = (id: string) => {
    track(apiResetDefect(orgId, inspectionId, id));
    setDefects((p) => p.flatMap((d) => {
      if (d.id !== id) return [d];
      if (d.source === "user") return [];
      return [{ ...d, ...d.orig, modified: false, editedBy: undefined, seeded: false } as Defect];
    }));
  };
  const deleteDefect = (id: string) => {
    const d = defects.find((x) => x.id === id);
    if (d) setDeleted((q) => [{ ...d, deletedBy: data.currentUserId, deletedAt: Date.now() }, ...q]);
    setDefects((p) => p.filter((x) => x.id !== id));
    setSelectedId((sel) => (sel === id ? null : sel));
    track(apiDeleteDefect(orgId, inspectionId, id));
  };
  const restoreDefect = (id: string) => {
    const d = deleted.find((x) => x.id === id);
    if (!d) return;
    const { deletedBy: _by, deletedAt: _at, ...rest } = d;
    setDeleted((q) => q.filter((x) => x.id !== id));
    setDefects((p) => [...p, rest as Defect]);
    setSelectedId(id);
    setMovingId(null);
    setFocus((f) => ({ id, seq: f.seq + 1 }));
    track(apiRestoreDefect(orgId, inspectionId, id));
  };
  const purgeDefect = (id: string) => { track(apiPurgeDefect(orgId, inspectionId, id)); setDeleted((q) => q.filter((x) => x.id !== id)); };
  // Re-point defects from a temporary (optimistic) photo id to its persisted id.
  const remapPhotoId = (oldId: string, newId: string) =>
    setDefects((p) => p.map((d) => (d.view === oldId ? { ...d, view: newId } : d)));

  // §7 #5: defects belong to ONE surface — everything the workspace renders
  // (pins, list, counts, filters, bin) is scoped to the ACTIVE surface: the
  // current ortho, or the current ad hoc photo. Cross-surface aggregates live
  // in the Overview tab, not here.
  // "photos" (no id) is the empty photos surface: reachable to add the first
  // photo, scopes to no defects until one exists.
  const inPhotos = view === PHOTOS_EMPTY || view.startsWith(PHOTO_PREFIX);
  const photoView = photoIdOf(view);
  const activeOrthoId = inPhotos || view === "model" ? undefined : data.orthos.some((o) => o.id === view) ? view : data.orthos[0]?.id;
  const onSurface = (d: Defect) => (inPhotos ? d.view === photoView : !d.view && d.orthoViewId === activeOrthoId);
  const scoped = useMemo(() => defects.filter(onSurface), [defects, view, activeOrthoId]);
  const scopedDeleted = useMemo(() => deleted.filter(onSurface), [deleted, view, activeOrthoId]);

  const dirtyCount = scoped.filter((d) => (d.source === "user" || d.modified) && !d.seeded).length;
  const counts = useMemo(() => {
    const c: Partial<Record<string, number>> = { all: scoped.length };
    SEV_ORDER.forEach((s) => { c[s] = scoped.filter((d) => d.severity === s).length; });
    return c;
  }, [scoped]);
  const catCounts = useMemo(() => {
    const c: Partial<Record<string, number>> = { all: scoped.length };
    CAT_ORDER.forEach((k) => { c[k] = 0; });
    scoped.forEach((d) => { c[catOf(d)] = (c[catOf(d)] ?? 0) + 1; });
    return c;
  }, [scoped]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter((d) => (filterSev === "all" || d.severity === filterSev) && (filterCat === "all" || catOf(d) === filterCat)
      && (!q || d.title.toLowerCase().includes(q) || d.desc.toLowerCase().includes(q) || ("#" + d.ref).includes(q) || catLabel(catOf(d)).toLowerCase().includes(q)))
      .sort((a, b) => sevMeta(b.severity).rank - sevMeta(a.severity).rank || a.ref - b.ref);
  }, [scoped, search, filterSev, filterCat]);

  return {
    defects, scoped, scopedDeleted, view, setView, selectedId, select, focus, focusDefect, tool, setTool, movingId, setMovingId,
    search, setSearch, filterSev, setFilterSev, filterCat, setFilterCat, addAt, addShape, updateShape, moveDefect, commitMove, updateDefect, resetDefect, deleteDefect,
    dirtyCount, counts, catCounts, filtered, deleted, restoreDefect, purgeDefect, remapPhotoId, saveStatus,
  };
}
type DefectStore = ReturnType<typeof useDefectStore>;

// ── Button ────────────────────────────────────────────────────────────
function Btn({ variant = "default", onClick, disabled, children, style, size = "md" }: {
  variant?: "default" | "outline" | "ghost" | "destructive";
  onClick?: () => void;
  disabled?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  size?: "sm" | "md";
}) {
  const [h, setH] = useState(false);
  const pad = size === "sm" ? "7px 12px" : "9px 15px";
  const fs = size === "sm" ? 12.5 : 13.5;
  let bg: string, col: string, bd: string;
  if (variant === "default") { bg = h ? "var(--di-orange-hover)" : "var(--di-orange)"; col = "#160d03"; bd = "transparent"; }
  else if (variant === "outline") { bg = h ? "rgba(255,255,255,0.04)" : "transparent"; col = h ? "var(--di-orange)" : "var(--di-text)"; bd = h ? "var(--di-orange)" : "var(--di-border)"; }
  else if (variant === "destructive") { bg = h ? "rgba(239,68,68,0.14)" : "rgba(239,68,68,0.10)"; col = "var(--di-destructive)"; bd = "rgba(239,68,68,0.30)"; }
  else { bg = h ? "rgba(255,255,255,0.05)" : "transparent"; col = "var(--di-muted-2)"; bd = "transparent"; }
  return (
    <button onClick={onClick} disabled={disabled} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, padding: pad, borderRadius: 9, border: `1px solid ${bd}`, background: bg, color: col, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1, fontFamily: "var(--font-sans)", fontSize: fs, fontWeight: 600, whiteSpace: "nowrap", transition: "all 150ms ease", ...style }}>{children}</button>
  );
}

// ── Tool hint (top-center pill while a marking tool is active) ────────
const TOOL_HINTS: Partial<Record<MarkTool, string>> = {
  point: "Click on the map to place a defect",
  polygon: "Click to outline the defect · click the first point to close",
  "measure-line": "Click points to measure · double-click or Enter to finish · Esc to clear",
  "measure-area": "Click to outline an area · close on the first point · Esc to clear",
};
function ToolHint({ tool, redraw }: { tool: MarkTool; redraw: boolean }) {
  const text = redraw ? "Redraw the outline · click the first point to close" : TOOL_HINTS[tool];
  if (!text) return null;
  return <div style={{ position: "absolute", top: 64, left: "50%", transform: "translateX(-50%)", zIndex: 60, display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999, background: "var(--di-orange)", color: "#160d03", fontFamily: "var(--font-sans)", fontSize: 12.5, fontWeight: 600, boxShadow: "0 8px 24px rgba(249,115,22,0.4)", whiteSpace: "nowrap" }}>{text}</div>;
}

// ── Autosave toast (subtle, bottom-center) ────────────────────────────
function SaveToast({ status }: { status: SaveStatus }) {
  if (status === "idle") return null;
  const saving = status === "saving";
  return (
    <div className="toast-in" style={{ position: "absolute", left: "50%", bottom: 18, transform: "translateX(-50%)", zIndex: 80, display: "flex", alignItems: "center", gap: 9, padding: "8px 15px 8px 12px", borderRadius: 999, background: "rgba(14,18,25,0.94)", border: "1px solid var(--di-border)", backdropFilter: "blur(8px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}>
      {saving
        ? <span className="spin" style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.18)", borderTopColor: "var(--di-orange)", flexShrink: 0 }}></span>
        : <span style={{ width: 17, height: 17, borderRadius: "50%", background: "var(--di-success-faded)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><IcCheck size={11} color="var(--di-success)" sw={3} /></span>}
      <span style={{ fontFamily: "var(--font-sans)", fontSize: 12.5, fontWeight: 500, color: saving ? "var(--di-muted-2)" : "var(--di-text)", whiteSpace: "nowrap" }}>{saving ? "Saving changes…" : "All changes saved"}</span>
    </div>
  );
}

// ── Editor modal ──────────────────────────────────────────────────────
function EditorModal({ defect, onClose, onSave }: { defect: Defect; onClose: () => void; onSave: (draft: DefectDraft) => void }) {
  const [draft, setDraft] = useState<DefectDraft>({ title: defect.title, category: catOf(defect), severity: defect.severity, desc: defect.desc });
  const isNew = defect.source === "user" && !defect.desc && defect.title === "New defect";
  return (
    <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(6,9,13,0.66)", backdropFilter: "blur(6px)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} className="pop-in" style={{ width: 440, maxWidth: "100%", background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 18, overflow: "visible" }}>
        <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--di-orange)" }}>#{defect.ref}</span>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.02em" }}>{isNew ? "New defect" : "Edit defect"}</h2>
            </div>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--di-border)", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--di-subtle)" }}><IcX size={14} /></button>
          </div>
          <DefectForm draft={draft} setDraft={setDraft} defect={defect} />
        </div>
        <div style={{ padding: "14px 22px", borderTop: "1px solid var(--di-border)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn variant="default" disabled={!draft.title.trim()} onClick={() => onSave(draft)}>{isNew ? "Add defect" : "Save changes"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Confirm (single defect delete / reset) ────────────────────────────
function ConfirmModal({ kind, defect, onClose, onConfirm }: { kind: "delete" | "reset"; defect: Defect; onClose: () => void; onConfirm: () => void }) {
  const isDelete = kind === "delete";
  const userAdded = defect.source === "user";
  const accent = isDelete ? "var(--di-destructive)" : "var(--di-orange)";
  const ring = isDelete ? { bg: "rgba(239,68,68,0.10)", bc: "rgba(239,68,68,0.26)" } : { bg: "rgba(249,115,22,0.10)", bc: "rgba(249,115,22,0.26)" };
  const Icon = isDelete ? IcTrash : IcUndo;
  const tag = <strong style={{ color: "var(--di-text)" }}>#{defect.ref} {defect.title}</strong>;
  const title = isDelete ? "Delete this defect?" : userAdded ? "Remove this defect?" : "Reset this defect?";
  const cta = isDelete ? "Delete" : userAdded ? "Remove" : "Reset";
  const body = isDelete
    ? <>This moves {tag} to the bin. You can restore it from the defects panel at any time.</>
    : userAdded
      ? <>A defect you added has no AI original to restore, so this removes {tag}.</>
      : <>This discards your changes and restores the AI's original detection of {tag}.</>;
  return (
    <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(6,9,13,0.66)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="pop-in" style={{ width: 400, background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 18, overflow: "hidden" }}>
        <div style={{ padding: "22px 22px 18px", display: "flex", flexDirection: "column", gap: 11 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: ring.bg, border: `1px solid ${ring.bc}`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={18} color={accent} /></div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.015em" }}>{title}</h2>
          <p style={{ fontSize: 13.5, color: "var(--di-muted)", lineHeight: 1.55 }}>{body}</p>
        </div>
        <div style={{ padding: "14px 22px", borderTop: "1px solid var(--di-border)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn variant={isDelete ? "destructive" : "default"} onClick={onConfirm}><Icon size={14} />{cta}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Deleted-defects bin (soft delete → restore) ───────────────────────
function binAgo(ts: number) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  return Math.floor(s / 86400) + "d ago";
}
function BinRow({ d, onRestore, onPurge }: { d: DeletedDefect; onRestore: () => void; onPurge: () => void }) {
  const [h, setH] = useState(false);
  const m = sevMeta(d.severity);
  const who = userName(d.deletedBy);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 12px", borderBottom: "1px solid var(--di-border)", background: h ? "rgba(255,255,255,0.03)" : "transparent" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.color, flexShrink: 0 }}></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--di-muted-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><span style={{ fontFamily: "var(--font-mono)", color: "var(--di-subtle)", marginRight: 5 }}>#{d.ref}</span>{d.title}</div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--di-subtle)", marginTop: 2 }}>Deleted{who ? " by " + who : ""} · {binAgo(d.deletedAt)}</div>
      </div>
      <button onClick={onRestore} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 7, border: "1px solid rgba(249,115,22,0.4)", background: "transparent", color: "var(--di-orange)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 11.5, fontWeight: 600, flexShrink: 0 }}><IcUndo size={11} color="var(--di-orange)" />Restore</button>
      <button onClick={onPurge} title="Delete permanently" style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid var(--di-border)", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}><IcX size={12} color="var(--di-subtle)" /></button>
    </div>
  );
}
function DeletedBin({ s }: { s: DefectStore }) {
  const [open, setOpen] = useState(false);
  const n = s.scopedDeleted.length;
  return (
    <div style={{ flexShrink: 0, borderTop: "1px solid var(--di-border)" }}>
      {open && n > 0 &&
        <div style={{ maxHeight: 196, overflowY: "auto", borderBottom: "1px solid var(--di-border)", background: "var(--di-void)" }}>
          {s.scopedDeleted.map((d) => <BinRow key={d.id} d={d} onRestore={() => s.restoreDefect(d.id)} onPurge={() => s.purgeDefect(d.id)} />)}
        </div>}
      <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: s.dirtyCount ? "var(--di-orange)" : "var(--di-subtle)" }}>{s.dirtyCount ? `${s.dirtyCount} change${s.dirtyCount === 1 ? "" : "s"}` : "No changes"}</span>
        {n > 0 &&
          <button onClick={() => setOpen((o) => !o)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 7, border: `1px solid ${open ? "var(--di-input)" : "var(--di-border)"}`, background: open ? "rgba(255,255,255,0.04)" : "transparent", color: "var(--di-muted-2)", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <IcTrash size={12} color="var(--di-subtle)" />Bin {n}<IcChev size={11} color="var(--di-subtle)" style={{ transform: open ? "rotate(180deg)" : "none" }} />
          </button>}
      </div>
    </div>
  );
}

// ── Ad hoc photo dock (bottom-left on photo views) ────────────────────
// Current photo's caption + capture metadata, a filmstrip to switch photos,
// and an Add tile that uploads more from the device. The per-photo defect
// count is a neutral mono chip (not an orange pin-like circle).
export function PhotoDock({ photos, active, defects, onPick, onAdd, readOnly }: {
  photos: PhotoSurface[]; active: PhotoSurface | null; defects: Defect[];
  onPick: (id: string) => void; onAdd?: (files: FileList | null) => void; readOnly?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const idx = active ? photos.findIndex((p) => p.id === active.id) : -1;
  return (
    <div style={{ position: "absolute", left: 14, bottom: 14, zIndex: 50, display: "flex", flexDirection: "column", gap: 8, padding: "9px 10px", background: "rgba(11,15,22,0.92)", border: "1px solid var(--di-border)", borderRadius: 14, backdropFilter: "blur(14px)", maxWidth: "min(480px, calc(100% - 110px))" }}>
      {active &&
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ width: 24, height: 24, borderRadius: 7, background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><IcCamera size={12} color="var(--di-orange)" /></span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--di-text)", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{active.label}<span style={{ fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, color: "var(--di-subtle)", marginLeft: 7 }}>{idx + 1} / {photos.length}</span></div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--di-subtle)", marginTop: 2, whiteSpace: "nowrap" }}>Ad hoc photo</div>
          </div>
        </div>}
      <div style={{ display: "flex", alignItems: "stretch", gap: 7, overflowX: "auto" }}>
        {photos.map((p) => {
          const on = !!active && p.id === active.id;
          const n = defects.filter((d) => d.view === p.id).length;
          return (
            <button key={p.id} onClick={() => onPick(p.id)} title={p.label} style={{ position: "relative", width: 76, padding: 0, border: `2px solid ${on ? "var(--di-orange)" : "transparent"}`, borderRadius: 9, overflow: "hidden", cursor: "pointer", background: "var(--di-ink)", opacity: on ? 1 : 0.6, transition: "opacity 150ms ease", flexShrink: 0 }}>
              <img src={p.src} alt={p.label} style={{ width: "100%", height: 48, objectFit: "cover", display: "block" }} />
              <span style={{ display: "block", padding: "3px 5px", fontFamily: "var(--font-mono)", fontSize: 7.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: on ? "var(--di-text)" : "var(--di-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", background: "rgba(14,18,25,0.9)", textAlign: "left" }}>{p.label}</span>
              {n > 0 && <span title={`${n} defect${n === 1 ? "" : "s"} tagged on this photo`} style={{ position: "absolute", top: 3, right: 3, padding: "1px 5px", borderRadius: 5, background: "rgba(11,15,22,0.88)", border: "1px solid var(--di-border)", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, color: "var(--di-text)", lineHeight: 1.5 }}>{n}</span>}
            </button>
          );
        })}
        {!readOnly && <button onClick={() => fileRef.current?.click()} title="Add ad hoc photos from your device"
          style={{ width: 76, flexShrink: 0, borderRadius: 9, border: "1.5px dashed var(--di-input)", background: "transparent", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, color: "var(--di-muted-2)" }}>
          <IcPlus size={14} color="var(--di-muted-2)" /><span style={{ fontFamily: "var(--font-mono)", fontSize: 7.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Add</span>
        </button>}
        {!readOnly && <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { onAdd?.(e.target.files); e.target.value = ""; }} />}
      </div>
    </div>
  );
}

// ── Empty ad hoc photos state (no photo uploaded yet) ─────────────────
// Reachable from the Photos switcher even with zero photos, so the first one
// can be added. Once a photo uploads, the view switches to it (the dock takes over).
function EmptyPhotos({ onAdd }: { onAdd: (files: FileList | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <div style={{ position: "absolute", inset: 0, background: "var(--di-void)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, textAlign: "center", padding: 24 }}>
      <div style={{ width: 56, height: 56, borderRadius: 15, background: "rgba(249,115,22,0.10)", border: "1px solid rgba(249,115,22,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <IcCamera size={26} color="var(--di-orange)" />
      </div>
      <div style={{ maxWidth: 380 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.01em" }}>No ad hoc photos yet</div>
        <p style={{ fontSize: 13, color: "var(--di-muted)", lineHeight: 1.55, marginTop: 6 }}>Add handheld photos from your device to tag defects the orthomosaic or 3D model did not capture, like sun-blown elevations or occluded corners.</p>
      </div>
      <Btn variant="default" onClick={() => fileRef.current?.click()}><IcPlus size={14} color="#160d03" />Add ad hoc photos</Btn>
      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { onAdd(e.target.files); e.target.value = ""; }} />
    </div>
  );
}

// ── Workspace (canvas-first) ──────────────────────────────────────────
interface SrcEdit { active: string[]; bin: string[] }

export function DefectWorkspace({ users, data }: { users?: OrgUser[]; data: WorkspaceData }) {
  const s = useDefectStore(data);
  const [editing, setEditing] = useState<Defect | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "delete" | "reset"; defect: Defect } | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [hideDefects, setHideDefects] = useState(false);
  const [layout, setLayout] = useState<"map" | "table">("map");
  const [detailId, setDetailId] = useState<string | null>(null); // full-view sheet (table layout)
  const [redrawId, setRedrawId] = useState<string | null>(null); // outlined defect being redrawn
  const [gallery, setGallery] = useState<{ defect: Defect; index: number } | null>(null);

  // ── Ad hoc photos: extra taggable surfaces alongside the ortho / 3D ──
  const [photos, setPhotos] = useState<PhotoSurface[]>(data.photoSurfaces);
  const isPhoto = s.view === PHOTOS_EMPTY || s.view.startsWith(PHOTO_PREFIX);
  const photo = s.view.startsWith(PHOTO_PREFIX) ? photos.find((p) => asPhotoView(p.id) === s.view) ?? null : null;
  const photoById = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);
  const lastPhoto = useRef<string | null>(null);
  useEffect(() => { if (s.view.startsWith(PHOTO_PREFIX)) lastPhoto.current = s.view; }, [s.view]);
  // A specific photo removed while viewing it: fall back to the empty photos
  // surface (or the first ortho). The empty "photos" view is intentional.
  useEffect(() => {
    if (s.view.startsWith(PHOTO_PREFIX) && !photo) s.setView(photos.length ? PHOTOS_EMPTY : data.orthos[0]?.id ?? PHOTOS_EMPTY);
  }, [photo]);
  const changeView = (v: string) => {
    const surf = v === PHOTOS_EMPTY ? "__none__" : photoIdOf(v);
    const sel = s.defects.find((d) => d.id === s.selectedId);
    if (sel && v !== "model" && (sel.view ?? null) !== surf) s.select(null);
    s.setView(v);
  };
  const openPhotos = () => {
    if (isPhoto) return;
    if (!photos.length) { changeView(PHOTOS_EMPTY); return; } // empty state: reach the Add tile
    const lp = lastPhoto.current;
    changeView(lp && photos.some((p) => asPhotoView(p.id) === lp) ? lp : asPhotoView(photos[0]!.id));
  };
  // Upload from the device: read dimensions on load, show the photo optimistically
  // (local object URL), then persist to R2 + the surface table and swap in the id.
  const addPhotoFiles = (files: FileList | null) => {
    Array.from(files ?? []).filter((f) => f.type.startsWith("image/")).forEach((f) => {
      const url = URL.createObjectURL(f);
      const img = new Image();
      img.onload = () => {
        const label = (f.name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim().slice(0, 40) || "Ad hoc photo";
        const w = img.naturalWidth || 1600, h = img.naturalHeight || 1200;
        const tempId = "ah-u" + Math.random().toString(36).slice(2);
        setPhotos((p) => [...p, { id: tempId, ref: 0, label, src: url, width: w, height: h, uploaded: true }]);
        changeView(asPhotoView(tempId));
        apiUploadPhotoSurface(data.orgId, data.inspectionId, f, { label, width: w, height: h })
          .then((real) => {
            s.remapPhotoId(tempId, real.id);
            setPhotos((p) => p.map((x) => (x.id === tempId ? { ...real, src: url } : x)));
            s.setView((cur) => (cur === asPhotoView(tempId) ? asPhotoView(real.id) : cur));
          })
          .catch((e) => console.error("photo upload failed:", e));
      };
      img.src = url;
    });
  };
  // Per-defect source-image edits: { [defectId]: { active: [imgIds], bin: [imgIds] } }
  const [srcEdits, setSrcEdits] = useState<Record<string, SrcEdit>>({});
  // Real captures: defect.sourceImages basenames resolve through the
  // inspection's photos (med thumb for display, raw URL for the original).
  const photoByBasename = useMemo(() => {
    const m = new Map<string, SourceImage>();
    data.photos.forEach((p) => m.set(p.basename, { id: p.basename, src: p.thumbMed ?? p.thumbSmall ?? p.url, full: p.url }));
    return m;
  }, [data.photos]);
  const defectSources = (d: Defect): SourceImage[] => {
    // Photo-tagged defect: its single source is the ad hoc photo it lives on.
    if (d.view) { const p = photoById.get(d.view); return p ? [{ id: asPhotoView(p.id), src: p.src, full: p.src }] : []; }
    return (d.sourceImages ?? []).map((b) => photoByBasename.get(b.replace(/\.[A-Za-z0-9]+$/, ""))).filter((v): v is SourceImage => !!v);
  };
  const getSourceById = (id: string): SourceImage | undefined => {
    const pid = photoIdOf(id);
    if (pid) { const p = photoById.get(pid); return p ? { id, src: p.src, full: p.src } : undefined; }
    return photoByBasename.get(id);
  };
  const srcFor = (m: Record<string, SrcEdit>, d: Defect): SrcEdit => m[d.id] ?? { active: defectSources(d).map((v) => v.id), bin: [] };
  const mutateSrc = (d: Defect, fn: (e: SrcEdit) => SrcEdit) => setSrcEdits((m) => ({ ...m, [d.id]: fn(srcFor(m, d)) }));
  const resolveSrc = (d: Defect): SourceImage[] => srcFor(srcEdits, d).active.map(getSourceById).filter((v): v is SourceImage => !!v);
  // Freshly created defects: the system takes ~5s to match the closest drone
  // captures — sources show a "calculating" state until the timer clears.
  const [calcIds, setCalcIds] = useState<Record<string, boolean>>({});
  const beginCalc = (id: string) => {
    setCalcIds((m) => ({ ...m, [id]: true }));
    setTimeout(() => setCalcIds((m) => { const n = { ...m }; delete n[id]; return n; }), 5000);
  };
  const srcCalculating = (d: Defect) => !!calcIds[d.id];
  useEffect(() => {
    setDefectUsers(users ?? []);
  }, [users]);

  const viewerProps = {
    view: s.view, defects: s.scoped, selectedId: s.selectedId, tool: s.tool, movingId: s.movingId,
    filterSev: s.filterSev, filterCat: s.filterCat,
    onAdd: (x: number, y: number) => {
      // A defect on an ad hoc photo has no drone captures to match: skip the calc.
      void s.addAt(x, y).then((nd) => { if (nd) { if (!nd.view) beginCalc(nd.id); setEditing(nd); } });
    },
    onAddShape: (shape: DefectShape) => {
      if (redrawId) { s.updateShape(redrawId, shape); setRedrawId(null); }
      else void s.addShape(shape).then((nd) => { if (nd) { if (!nd.view) beginCalc(nd.id); setEditing(nd); } });
    },
    onExitTool: () => { s.setTool("select"); setRedrawId(null); },
    onMove: s.moveDefect, onMoveEnd: s.commitMove, onSelect: s.select,
    onEdit: (d: Defect) => setEditing(d),
    onReset: (d: Defect) => setConfirm({ kind: "reset", defect: d }),
    onDelete: (d: Defect) => setConfirm({ kind: "delete", defect: d }),
    onMoveToggle: (id: string) => {
      const d = s.defects.find((v) => v.id === id);
      // Outlined defects aren't dragged — Redraw puts you back in the polygon
      // tool and the finished outline replaces the old one.
      if (d && d.shape) { setRedrawId(id); s.setTool("polygon"); s.select(null); }
      else s.setMovingId((cur) => (cur === id ? null : id));
    },
    focus: s.focus,
    onViewSources: (d: Defect, i = 0) => { if (calcIds[d.id]) return; setGallery({ defect: d, index: i }); },
    resolveSources: resolveSrc,
    sourcesCalculating: srcCalculating,
    hideDefects,
  };

  // The surface fed to the Viewer: an ad hoc photo renders as a flat CRS.Simple
  // image plane (photoSrc); orthos render their tile pyramid.
  const photoSurface: ActiveOrtho | null = photo
    ? { id: photo.id, tileUrl: "", width: photo.width, height: photo.height, maxZoom: 0, tileSize: 256, gsdM: null, photoSrc: photo.src }
    : null;
  const currentOrtho = isPhoto ? photoSurface : (data.orthos.find((o) => o.id === s.view) ?? data.orthos[0] ?? null);
  const wsViews: ViewDef[] = [
    ...data.orthos.map((o) => ({ id: o.id, label: o.label, Ic: o.view === "top" ? IcLayers : IcImage, available: true })),
    ...(data.modelUrl ? [{ id: "model", label: "3D", Ic: IcCube, available: true }] : []),
  ];

  return (
    <div style={{ position: "absolute", inset: 0, background: "var(--di-void)", overflow: "hidden" }}>
      {layout === "map" && s.view === "model" && data.modelUrl && <Model3DView url={data.modelUrl} />}
      {/* Empty ad hoc photos surface: no photo yet — show the add-first-photo state. */}
      {layout === "map" && isPhoto && !photo && <EmptyPhotos onAdd={addPhotoFiles} />}
      {layout === "map" && s.view !== "model" && currentOrtho && <Viewer key={s.view} ortho={currentOrtho} {...viewerProps}>
        {/* top-right tools */}
        <div style={{ position: "absolute", top: 14, right: 14, zIndex: 50, display: "flex", gap: 8 }}>
          <button onClick={() => setHideDefects((h) => !h)} title={hideDefects ? "Show defects" : "Hide defects"} style={{ width: 36, height: 34, borderRadius: 9, border: "1px solid var(--di-border)", background: hideDefects ? "var(--di-orange)" : "rgba(14,18,25,0.86)", color: hideDefects ? "#160d03" : "var(--di-muted-2)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(8px)" }}>{hideDefects ? <IcEyeOff size={15} color="#160d03" /> : <IcEye size={15} color="var(--di-muted-2)" />}</button>
          <button onClick={() => setPanelOpen((o) => !o)} title="Toggle defect list" style={{ width: 36, height: 34, borderRadius: 9, border: "1px solid var(--di-border)", background: panelOpen ? "var(--di-orange)" : "rgba(14,18,25,0.86)", color: panelOpen ? "#160d03" : "var(--di-muted-2)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(8px)" }}><IcList size={15} color={panelOpen ? "#160d03" : "var(--di-muted-2)"} /></button>
        </div>
        {/* marking tools palette (left; higher on photos where there is no compass) */}
        <div style={{ position: "absolute", left: 14, top: isPhoto ? 66 : 120, zIndex: 50 }}>
          <ToolPalette tool={s.tool} setTool={(t) => { s.select(null); s.setTool(t); }} measure={!isPhoto} />
        </div>
        {/* legend bottom-left on orthos · photo dock on ad hoc photos */}
        {!isPhoto && <div style={{ position: "absolute", left: 14, bottom: 14, zIndex: 50 }}><Legend floating /></div>}
        {isPhoto && <PhotoDock photos={photos} active={photo} defects={s.defects} onPick={(id) => changeView(asPhotoView(id))} onAdd={addPhotoFiles} />}
        {s.tool !== "select" && <ToolHint tool={s.tool} redraw={!!redrawId} />}
        {/* floating defects panel */}
        {panelOpen && (
          <div style={{ position: "absolute", top: 66, right: 14, bottom: 14, width: 322, zIndex: 45, background: "rgba(11,15,22,0.92)", border: "1px solid var(--di-border)", borderRadius: 16, backdropFilter: "blur(14px)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <DefectList defects={s.filtered} selectedId={s.selectedId} onSelect={s.focusDefect} search={s.search} setSearch={s.setSearch}
              filterSev={s.filterSev} setFilterSev={s.setFilterSev} counts={s.counts} filterCat={s.filterCat} setFilterCat={s.setFilterCat} catCounts={s.catCounts} compact
              headerRight={<button onClick={() => setPanelOpen(false)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid var(--di-border)", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--di-subtle)" }}><IcX size={13} /></button>} />
            <DeletedBin s={s} />
          </div>
        )}
      </Viewer>}

      {layout === "table" &&
        <DefectTable defects={s.filtered} selectedId={s.selectedId} onSelect={(id) => { s.select(id); setDetailId(id); }}
          search={s.search} setSearch={s.setSearch} filterSev={s.filterSev} setFilterSev={s.setFilterSev} counts={s.counts} filterCat={s.filterCat} setFilterCat={s.setFilterCat} catCounts={s.catCounts}
          resolveSources={resolveSrc} sourcesCalculating={srcCalculating} onViewSources={(d, i = 0) => { if (calcIds[d.id]) return; setGallery({ defect: d, index: i }); }}
          onEdit={(d) => setEditing(d)} onReset={(d) => setConfirm({ kind: "reset", defect: d })} onDelete={(d) => setConfirm({ kind: "delete", defect: d })}
          onResetAll={null}
          footer={<DeletedBin s={s} />} />}

      {layout === "table" && detailId && (() => {
        const d = s.defects.find((x) => x.id === detailId);
        if (!d) return null; // deleted while open
        return <DefectDetail d={d} sources={resolveSrc(d)} calculating={srcCalculating(d)}
          escEnabled={!gallery && !editing && !confirm}
          onClose={() => setDetailId(null)}
          onEdit={(x) => setEditing(x)} onReset={(x) => setConfirm({ kind: "reset", defect: x })} onDelete={(x) => setConfirm({ kind: "delete", defect: x })}
          onViewSources={(x, i = 0) => { if (calcIds[x.id]) return; setGallery({ defect: x, index: i }); }} />;
      })()}

      {/* top-left: workspace mode + view switcher */}
      <div style={{ position: "absolute", top: 14, left: 14, zIndex: 50, display: "flex", gap: 8 }}>
        <div style={{ background: "rgba(14,18,25,0.86)", border: "1px solid var(--di-border)", borderRadius: 12, padding: 4, backdropFilter: "blur(8px)" }}>
          <WorkspaceModeToggle mode={layout} setMode={(m) => { setLayout(m); s.setMovingId(null); s.setTool("select"); setRedrawId(null); }} />
        </div>
        {layout === "map" &&
          <div style={{ background: "rgba(14,18,25,0.86)", border: "1px solid var(--di-border)", borderRadius: 12, padding: 4, backdropFilter: "blur(8px)" }}>
            <ViewSwitcher view={s.view} setView={changeView} defectCounts={s.scoped.length} views={wsViews}
              photos={{ count: photos.length, active: isPhoto, onClick: openPhotos, canAdd: true }} />
          </div>}
      </div>

      <SaveToast status={s.saveStatus} />

      {gallery && (() => {
        const d = gallery.defect;
        const e = srcFor(srcEdits, d);
        const act = e.active.map(getSourceById).filter((v): v is SourceImage => !!v);
        const binned = e.bin.map(getSourceById).filter((v): v is SourceImage => !!v);
        const catalog = data.photos
          .map((p) => photoByBasename.get(p.basename))
          .filter((v): v is SourceImage => !!v && !e.active.includes(v.id) && !e.bin.includes(v.id))
          .slice(0, 300);
        return <SourceGallery defect={d} sources={act} bin={binned} catalog={catalog}
          index={Math.max(0, Math.min(gallery.index, act.length - 1))}
          setIndex={(i) => setGallery((g) => g && { ...g, index: i })}
          onRemove={(id) => mutateSrc(d, (ed) => ({ active: ed.active.filter((a) => a !== id), bin: [...ed.bin, id] }))}
          onAdd={(id) => mutateSrc(d, (ed) => ({ ...ed, active: [...ed.active, id] }))}
          onRestore={(id) => mutateSrc(d, (ed) => ({ active: [...ed.active, id], bin: ed.bin.filter((b) => b !== id) }))}
          onClose={() => setGallery(null)} />;
      })()}
      {editing && <EditorModal defect={editing} onClose={() => setEditing(null)} onSave={(draft) => { s.updateDefect(editing.id, draft); setEditing(null); }} />}
      {confirm && <ConfirmModal kind={confirm.kind} defect={confirm.defect} onClose={() => setConfirm(null)} onConfirm={() => { if (confirm.kind === "delete") s.deleteDefect(confirm.defect.id); else s.resetDefect(confirm.defect.id); setConfirm(null); }} />}
    </div>
  );
}
